import { useCallback, useEffect, useMemo, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Box, RadioGroup, Slider, Typography } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';
import { ValuationApproachModel } from 'api';
import { PDF_EXPORT_INFO_MESSAGE } from 'common/constants/documents';
import { useStore } from 'common/store';
import { PdfExportConfig, TEMPLATE_CONSTANTS } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import {
  getDefaultConcurrency,
  MAX_CONCURRENCY,
  MIN_CONCURRENCY,
  usePdfExport,
} from 'features/pdfExport';
import { getAllocationStatus, Logger } from 'utilities';
import { useIsCompanyPath } from 'utilities/useIsCompanyPath/useIsCompanyPath';
import { useIsFundPath } from 'utilities/useIsFundPath/useIsFundPath';
import { CompanySubOptions } from './components/CompanySubOptions';
import { FirmSubOptions } from './components/FirmSubOptions';
import { FundSubOptions } from './components/FundSubOptions';
import { RadioOption } from './components/RadioOption';
import { useDialogValuationInfo } from './hooks/useDialogValuationInfo';
import { usePdfPages } from './hooks/usePdfPages';
import {
  PdfExportButton,
  StyledButton,
  StyledContentContainer,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  StyledInfoIcon,
  StyledRadioContainer,
} from './PdfExportConfigDialog.styles';
import {
  COMPANY_OPTION_CONFIGS,
  CompanyPdfApproachOptionKey,
  CompanyPdfOptions,
  DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH,
  DEFAULT_PDF_CONFIGURATION,
  PdfExportConfigDialogProps,
  PdfExportConfiguration,
  PdfExportOption,
} from './PdfExportConfigDialog.types';

const PdfExportConfigDialog = ({ open, onClose }: PdfExportConfigDialogProps) => {
  const [configuration, setConfiguration] = useState<PdfExportConfiguration>(DEFAULT_PDF_CONFIGURATION);
  const [concurrency, setConcurrency] = useState<number>(() => getDefaultConcurrency());

  // Determine current context
  const isCompanyPath = useIsCompanyPath();
  const isFundPath = useIsFundPath();
  const isFirmPath = useMemo(() => !isCompanyPath && !isFundPath, [isCompanyPath, isFundPath]);

  let coverTitle = 'PDF Report of';

  if (isCompanyPath) {
    coverTitle = 'Valuation Report of';
  } else if (isFundPath) {
    coverTitle = 'Fund Report of';
  } else if (isFirmPath) {
    coverTitle = 'Firm Report of';
  }

  // Get entity information from store
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { allocationInfo, companyInfo, firmInfo, fundInfo, fundList } = storeValues;
  const params = useParams();
  const location = useLocation();

  // Fetch the merged valuation directly so approach availability validation works on every
  // company tab, not only the Valuations page (which is the only place the store is populated).
  const { valuationInfo, isLoading: isLoadingDialogValuation } = useDialogValuationInfo({
    enabled: open && isCompanyPath,
  });

  // Get the current entity name based on context
  const entityName = useMemo(() => {
    if (isCompanyPath && companyInfo?.name) {
      return companyInfo.name;
    }
    if (isFundPath) {
      // First check fundInfo from store
      if (fundInfo?.name) {
        return fundInfo.name;
      }
      // Otherwise find it from fundList using slug
      const fundSlug = params.fundSlugParam;
      if (fundSlug && fundList) {
        const currentFund = fundList.find(fund => fund.slug === fundSlug);
        return `${currentFund?.name}`;
      }
    }
    if (isFirmPath && firmInfo?.name) {
      return firmInfo.name;
    }
    return '';
  }, [isCompanyPath, isFundPath, isFirmPath, companyInfo, firmInfo, fundInfo, fundList, params.fundSlugParam]);

  // Get measurement date from query string
  const measurementDate = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get('date') ?? '';
    if (raw === '' || raw === 'undefined') {
      return '';
    }
    return raw;
  }, [location.search]);

  // Generate dynamic filename based on entity name and date
  const filename = useMemo(() => {
    const parts = [];

    if (entityName) {
      // Sanitize entity name for filename (remove special characters)
      const sanitizedName = entityName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(sanitizedName);
    }

    if (measurementDate) {
      parts.push(measurementDate);
    }

    return parts.length > 0 ? parts.join('-') : 'pdf-export';
  }, [entityName, measurementDate]);

  // Get dynamic URLs based on current context and configuration
  const pages = usePdfPages(configuration, valuationInfo);

  const availableApproachKeys = useMemo(() => {
    const keys = new Set<CompanyPdfApproachOptionKey>();
    valuationInfo?.valuations_approaches?.forEach(approach => {
      // A GPC approach flagged as calibration backs a Calibration row and is
      // surfaced via valuationInfo.calibrations below; skip it here so it isn't
      // double-counted as Public Comps. The flag can be set on other approach
      // types in legacy data, so scope the skip to Public Comps only.
      if (
        approach.is_calibration
        && approach.approach_type === ValuationApproachModel.approach_type.PUBLIC_COMPS
      ) {
        return;
      }
      switch (approach.approach_type) {
        case ValuationApproachModel.approach_type.BACKSOLVE:
          if (approach.valuations_approach_backsolve?.applied_methodologies?.length) {
            keys.add('approachBacksolve');
          }
          break;
        case ValuationApproachModel.approach_type.PUBLIC_COMPS:
          if (approach.valuations_approach_gpc?.gpc_comparison?.length) {
            keys.add('approachPublicComps');
          }
          break;
        case ValuationApproachModel.approach_type.DISCOUNTED_CASH_FLOW:
          if (approach.valuations_approach_dcf?.dcf_financial_period?.length) {
            keys.add('approachDcf');
          }
          break;
        case ValuationApproachModel.approach_type.TRANSACTION_COMPS:
          if (approach.valuations_approach_gpt?.gpt_transactions?.length) {
            keys.add('approachTransactionComps');
          }
          break;
        case ValuationApproachModel.approach_type.FUTURE_EXIT:
          if (
            approach.valuations_approach_future_exit?.is_future_exit_calculated
            || approach.valuations_approach_future_exit?.present_share_values?.length
            || approach.valuations_approach_future_exit?.future_share_values?.length
          ) {
            keys.add('approachFutureExit');
          }
          break;
        case ValuationApproachModel.approach_type.SPECIFIED_SHARE_VALUES:
          // The merged page renders an SSV tab for any SSV approach, regardless
          // of whether share_values or cap_table have been populated yet, so
          // mirror that here instead of gating on the nested SSV payload.
          keys.add('approachSpecifiedShareValues');
          break;
        case ValuationApproachModel.approach_type.EXTERNAL_VALUATION:
          if (
            approach.valuations_approach_ev?.equity_value != null
            || approach.valuations_approach_ev?.enterprise_value != null
          ) {
            keys.add('approachExternalValuation');
          }
          break;
        default:
          break;
      }
    });

    // Calibrations live in their own array on the merged valuation, not within
    // valuations_approaches. A calibration tab renders only when the backing GPC
    // approach has comparable companies, so verify that against the related
    // approach (looked up via Calibration.public_comps_approach_calibration).
    const approachesById = new Map(
      valuationInfo?.valuations_approaches?.map(a => [a.id, a]) ?? []
    );
    const hasUsableCalibration = valuationInfo?.calibrations?.some(calibration => {
      const backing = approachesById.get(calibration.public_comps_approach_calibration ?? -1);
      return backing?.valuations_approach_gpc?.gpc_comparison?.length;
    });
    if (hasUsableCalibration) {
      keys.add('approachCalibration');
    }

    return keys;
  }, [valuationInfo]);

  const isExportDisabled = pages.length === 0 || isLoadingDialogValuation;

  // Sync companyOptionsOrder when valuation info loads. Only include approach
  // slots whose approach actually exists on the current valuation, so unavailable
  // approaches are hidden from the dialog and excluded from the export.
  useEffect(() => {
    if (!Array.isArray(valuationInfo?.valuations_approaches)) {
      return;
    }
    const nonApproachKeys = COMPANY_OPTION_CONFIGS.map(c => c.key) as Array<keyof CompanyPdfOptions>;
    const validKeys = new Set<keyof CompanyPdfOptions>([...nonApproachKeys, ...availableApproachKeys]);

    setConfiguration(prev => {
      const filtered = prev.companyOptionsOrder.filter(k => validKeys.has(k));
      const added = [...availableApproachKeys].filter(k => !filtered.includes(k));
      const missing = DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH.filter(k => !filtered.includes(k));
      const nextOrder = [...filtered, ...added, ...missing];
      if (
        nextOrder.length === prev.companyOptionsOrder.length &&
        nextOrder.every((k, i) => k === prev.companyOptionsOrder[i])
      ) {
        return prev;
      }
      return { ...prev, companyOptionsOrder: nextOrder };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableApproachKeys, valuationInfo]);

  const handleCompanyOrderChange = useCallback((order: Array<keyof CompanyPdfOptions>) => {
    setConfiguration(prev => ({ ...prev, companyOptionsOrder: order }));
  }, []);

  // Automatically set the selected option based on current context
  useEffect(() => {
    if (isCompanyPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.VALUATIONS }));
    } else if (isFundPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FUND }));
    } else if (isFirmPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FIRM }));
    }
  }, [isCompanyPath, isFundPath, isFirmPath]);

  const pdfExport = usePdfExport();

  const handleOptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value as PdfExportOption;
    setConfiguration(prev => {
      const newConfig = {
        ...prev,
        selectedOption,
      };

      if (selectedOption === PdfExportOption.VALUATIONS) {
        newConfig.companyOptions = {
          valuationSummary: true,
          equityAllocation: true,
          concludedShareValues: true,
          financials: true,
          fundOwnership: true,
          capTable: true,
          breakpointAnalysis: true,
          cashFlowLedger: true,
          approachBacksolve: true,
          approachDcf: true,
          approachExternalValuation: true,
          approachFutureExit: true,
          approachPublicComps: true,
          approachTransactionComps: true,
          approachSpecifiedShareValues: true,
          approachCalibration: true,
        };
      }

      return newConfig;
    });
  }, []);

  const handleCompanyOptionsChange = useCallback(
    (options: typeof configuration.companyOptions) => {
      setConfiguration(prev => ({
        ...prev,
        companyOptions: options,
      }));
    },
    [configuration]
  );

  const handleFundOptionsChange = useCallback(
    (options: typeof configuration.fundOptions) => {
      setConfiguration(prev => ({
        ...prev,
        fundOptions: options,
      }));
    },
    [configuration]
  );

  const handleFirmOptionsChange = useCallback(
    (options: typeof configuration.firmOptions) => {
      setConfiguration(prev => ({
        ...prev,
        firmOptions: options,
      }));
    },
    [configuration]
  );

  const handleExport = useCallback(() => {
    if (firmInfo) {
      const firmName = firmInfo.name;
      const isFirmOwned = valuationInfo?.is_firm_owned || false;
      const dateOfIssue = `Issue date: ${moment().format('MMMM D, YYYY')}`;

      const firmLogo = firmInfo.logo_url || TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const coverBackgroundImage = isFirmOwned
        ? TEMPLATE_CONSTANTS.COVER_LIGHT_BACKGROUND_JPG
        : TEMPLATE_CONSTANTS.COVER_BLUE_BACKGROUND_JPG;
      const coverLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_WHITE_LOGO_PNG;
      const coverSecondaryLogo = TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const pageLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const pageBackgroundImage = TEMPLATE_CONSTANTS.PAGES_BACKGROUND_JPG;
      const footerLogo = TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const infoIcon = TEMPLATE_CONSTANTS.INFO_ICON_PNG;

      let allocationStatus: number | undefined;
      if (allocationInfo) {
        const { is_final: isFinal, is_published: isPublished } = allocationInfo;
        allocationStatus = getAllocationStatus({ isFinal: Boolean(isFinal), isPublished: Boolean(isPublished) });
      }

      const config: PdfExportConfig = {
        filename,
        entityName,
        coverTitle,
        dateOfIssue,
        pages,
        isFirmOwned,
        coverBackgroundImage,
        coverLogo,
        coverSecondaryLogo,
        pageBackgroundImage,
        pageLogo,
        footerLogo,
        infoIcon,
        firmName,
        allocationStatus,
        measurementDate,
        concurrency,
      };

      onClose();
      pdfExport.start(config);
    } else {
      Logger.error(
        'Failed to export PDF: No valuation info or firm info found',
        new Error('Failed to export PDF: No valuation info or firm info found')
      );
      enqueueSnackbar('Failed to export PDF: No valuation info or firm info found', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [
    onClose,
    pages,
    filename,
    entityName,
    firmInfo,
    valuationInfo,
    coverTitle,
    allocationInfo,
    measurementDate,
    pdfExport,
    concurrency,
  ]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>{`${entityName} PDF Export`}</StyledDialogTitle>

      <StyledDialogContent>
        <StyledRadioContainer>
          <RadioGroup value={configuration.selectedOption} onChange={handleOptionChange}>
            {isCompanyPath && (
              <>
                <RadioOption value={PdfExportOption.VALUATIONS} label="Valuations" />
                <CompanySubOptions
                  options={configuration.companyOptions}
                  onOptionsChange={handleCompanyOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.VALUATIONS}
                  optionsOrder={configuration.companyOptionsOrder}
                  onOrderChange={handleCompanyOrderChange}
                />
              </>
            )}

            {isFundPath && (
              <>
                <RadioOption value={PdfExportOption.FUND} label="Fund" />
                <FundSubOptions
                  options={configuration.fundOptions}
                  onOptionsChange={handleFundOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FUND}
                />
              </>
            )}

            {isFirmPath && (
              <>
                <RadioOption value={PdfExportOption.FIRM} label="Firm" />
                <FirmSubOptions
                  options={configuration.firmOptions}
                  onOptionsChange={handleFirmOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FIRM}
                />
              </>
            )}
          </RadioGroup>
        </StyledRadioContainer>

        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2">Parallel page captures</Typography>
            <Typography variant="body2" color="text.secondary">
              {concurrency}
            </Typography>
          </Box>
          <Slider
            id="pdf-export-concurrency-slider"
            value={concurrency}
            min={MIN_CONCURRENCY}
            max={MAX_CONCURRENCY}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_event, value) => {
              if (typeof value === 'number') {
                setConcurrency(value);
              }
            }}
            data-testid="pdf-export-concurrency-slider"
          />
          <Typography variant="caption" color="text.secondary">
            Recommended for your computer. Move the slider down if the page feels stuck or slow.
          </Typography>
        </Box>

        <StyledContentContainer>
          <StyledInfoIcon>
            <InfoIcon color="action" />
          </StyledInfoIcon>
          <Typography>{PDF_EXPORT_INFO_MESSAGE}</Typography>
        </StyledContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose}>Cancel</StyledButton>
        <PdfExportButton onClick={handleExport} disabled={isExportDisabled} isExporting={false}>
          Export PDF
        </PdfExportButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default PdfExportConfigDialog;
